import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  FormField,
  Input,
  TextArea,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  Toggle,
  ConfirmDialog,
} from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import {
  fetchAdminBlogPosts,
  createBlogPost,
  updateBlogPost,
  publishBlogPost,
  unpublishBlogPost,
  deleteBlogPost,
  fetchHiddenStaticArticles,
  hideStaticArticle,
  unhideStaticArticle,
} from "../utils/adminApi";
import { loadStaticArticles } from "../../app/blog/utils/articleLoader";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

// ---------------------------------------------------------------------------
// New Post Form
// ---------------------------------------------------------------------------

const NewPostForm = ({ onCreated }) => {
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [content, setContent] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !author.trim() || !content.trim()) {
      setError("Title, author, and content are all required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await createBlogPost({ title, author, content, is_published: isPublished });
      setTitle("");
      setAuthor("");
      setContent("");
      setIsPublished(true);
      if (onCreated) onCreated();
    } catch (err) {
      setError(err?.message || "Failed to create blog post.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader title="New Blog Post" subtitle="Create a new blog post (content supports Markdown)" />
      <CardBody>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className={`rounded-2xl border border-red-200 dark:border-red-800 bg-red-100 dark:bg-red-900/30 px-4 py-3 ${typography.bodySmall} text-red-800 dark:text-red-300`}>
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Title" htmlFor="new_post_title">
              <Input
                id="new_post_title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Blog post title"
              />
            </FormField>

            <FormField label="Author" htmlFor="new_post_author">
              <Input
                id="new_post_author"
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Author name"
              />
            </FormField>
          </div>

          <FormField label="Content (Markdown)" htmlFor="new_post_content">
            <TextArea
              id="new_post_content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your blog post in Markdown..."
              rows={10}
            />
          </FormField>

          <div className="flex items-center gap-4">
            <label className={`${typography.label} ${textColors.secondary} flex items-center gap-2`}>
              Publish immediately
              <Toggle checked={isPublished} onChange={() => setIsPublished((v) => !v)} />
            </label>
          </div>

          <div className="flex items-center gap-3">
            <PrimaryButton type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create Post"}
            </PrimaryButton>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Edit Post Modal (inline editing)
// ---------------------------------------------------------------------------

const EditPostRow = ({ post, onSaved, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(post.title);
  const [author, setAuthor] = useState(post.author);
  const [content, setContent] = useState(post.content);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleSave = async () => {
    if (!title.trim() || !author.trim() || !content.trim()) {
      setError("All fields are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await updateBlogPost(post.id, { title, author, content });
      setEditing(false);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    setToggling(true);
    try {
      if (post.is_published) {
        await unpublishBlogPost(post.id);
      } else {
        await publishBlogPost(post.id);
      }
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.message || "Failed to toggle publish state.");
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteBlogPost(post.id);
      setShowDelete(false);
      if (onDeleted) onDeleted();
    } catch (err) {
      setError(err?.message || "Failed to delete.");
    }
  };

  if (editing) {
    return (
      <tr className={`border-b ${borders.glass}`}>
        <td colSpan={6} className="py-4 px-2">
          <div className="space-y-3">
            {error && (
              <div className={`rounded-2xl border border-red-200 dark:border-red-800 bg-red-100 dark:bg-red-900/30 px-3 py-2 ${typography.bodySmall} text-red-800 dark:text-red-300`}>
                {error}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Title" htmlFor={`edit_title_${post.id}`}>
                <Input
                  id={`edit_title_${post.id}`}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </FormField>
              <FormField label="Author" htmlFor={`edit_author_${post.id}`}>
                <Input
                  id={`edit_author_${post.id}`}
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                />
              </FormField>
            </div>
            <FormField label="Content (Markdown)" htmlFor={`edit_content_${post.id}`}>
              <TextArea
                id={`edit_content_${post.id}`}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
              />
            </FormField>
            <div className="flex items-center gap-3">
              <PrimaryButton onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </PrimaryButton>
              <SecondaryButton
                onClick={() => {
                  setEditing(false);
                  setTitle(post.title);
                  setAuthor(post.author);
                  setContent(post.content);
                  setError("");
                }}
              >
                Cancel
              </SecondaryButton>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      <tr className={`border-b ${borders.glass}`}>
        <td className={`py-2 pr-4 ${textColors.primary}`}>{post.title}</td>
        <td className={`py-2 pr-4 ${textColors.secondary}`}>{post.author}</td>
        <td className="py-2 pr-4">
          {post.is_published ? (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
              Published
            </span>
          ) : (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
              Draft
            </span>
          )}
        </td>
        <td className={`py-2 pr-4 whitespace-nowrap ${textColors.secondary}`}>
          {formatDateTime(post.created_at)}
        </td>
        <td className="py-2 pr-4">
          <div className="max-w-[280px] truncate" title={post.content}>
            <span className={textColors.muted}>{post.content}</span>
          </div>
        </td>
        <td className="py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <SecondaryButton onClick={() => setEditing(true)}>Edit</SecondaryButton>
            <SecondaryButton onClick={handleTogglePublish} disabled={toggling}>
              {toggling ? "…" : post.is_published ? "Unpublish" : "Publish"}
            </SecondaryButton>
            <DangerButton onClick={() => setShowDelete(true)}>Delete</DangerButton>
          </div>
          {error && <div className={`mt-1 text-red-600 dark:text-red-400 ${typography.caption}`}>{error}</div>}
        </td>
      </tr>

      <ConfirmDialog
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="Delete Blog Post"
        message={`Are you sure you want to permanently delete "${post.title}"? This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Static Articles Section
// ---------------------------------------------------------------------------

const StaticArticlesSection = ({ hiddenSlugs, onToggle }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingSlug, setTogglingSlug] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const loaded = await loadStaticArticles();
        setArticles(loaded || []);
      } catch {
        // static articles failed to load – non-critical
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleToggle = async (slug, currentlyHidden) => {
    setTogglingSlug(slug);
    try {
      if (currentlyHidden) {
        await unhideStaticArticle(slug);
      } else {
        await hideStaticArticle(slug);
      }
      if (onToggle) onToggle();
    } catch {
      // swallow – user can retry
    } finally {
      setTogglingSlug(null);
    }
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <CardHeader title="Static Articles" subtitle="Hardcoded .md articles bundled with the frontend" />
        <CardBody>
          <div className={`${typography.body} ${textColors.secondary}`}>Loading static articles…</div>
        </CardBody>
      </Card>
    );
  }

  if (articles.length === 0) return null;

  const hiddenSet = new Set(hiddenSlugs || []);

  return (
    <Card className="mb-6">
      <CardHeader
        title="Static Articles"
        subtitle="These articles are bundled in the frontend source code. You can hide/show them but not edit or delete them."
      />
      <CardBody>
        <div className="overflow-x-auto">
          <table className={`w-full text-left ${typography.bodySmall}`}>
            <thead>
              <tr className={`border-b ${borders.glass}`}>
                <th className={`py-2 pr-4 ${typography.label} ${textColors.secondary}`}>Title</th>
                <th className={`py-2 pr-4 ${typography.label} ${textColors.secondary}`}>Author</th>
                <th className={`py-2 pr-4 ${typography.label} ${textColors.secondary}`}>Date</th>
                <th className={`py-2 pr-4 ${typography.label} ${textColors.secondary}`}>Status</th>
                <th className={`py-2 ${typography.label} ${textColors.secondary}`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => {
                const isHidden = hiddenSet.has(article.slug);
                const isToggling = togglingSlug === article.slug;
                return (
                  <tr key={article.slug} className={`border-b ${borders.glass}`}>
                    <td className={`py-2 pr-4 ${textColors.primary}`}>{article.title}</td>
                    <td className={`py-2 pr-4 ${textColors.secondary}`}>{article.author}</td>
                    <td className={`py-2 pr-4 whitespace-nowrap ${textColors.secondary}`}>{article.date}</td>
                    <td className="py-2 pr-4">
                      {isHidden ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                          Hidden
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                          Visible
                        </span>
                      )}
                    </td>
                    <td className="py-2">
                      <SecondaryButton onClick={() => handleToggle(article.slug, isHidden)} disabled={isToggling}>
                        {isToggling ? "…" : isHidden ? "Show" : "Hide"}
                      </SecondaryButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Main BlogAdminPage
// ---------------------------------------------------------------------------

const BlogAdminPage = () => {
  const [posts, setPosts] = useState([]);
  const [hiddenSlugs, setHiddenSlugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [postsData, hiddenData] = await Promise.all([
        fetchAdminBlogPosts(),
        fetchHiddenStaticArticles(),
      ]);
      setPosts(postsData?.results || []);
      setHiddenSlugs(hiddenData?.slugs || []);
    } catch (err) {
      setError(err?.message || "Failed to load blog data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const publishedCount = useMemo(() => posts.filter((p) => p.is_published).length, [posts]);
  const draftCount = useMemo(() => posts.filter((p) => !p.is_published).length, [posts]);

  return (
    <div className={`min-h-screen ${surfaces.modernApp}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className={`${typography.h2} ${textColors.primary}`}>Blog Management</h1>
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>
              Create, edit, publish, unpublish, and delete blog posts.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <SecondaryButton onClick={loadData} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </SecondaryButton>
          </div>
        </div>

        {error && (
          <div className={`mb-4 rounded-2xl border border-red-200 dark:border-red-800 bg-red-100 dark:bg-red-900/30 px-4 py-3 ${typography.bodySmall} text-red-800 dark:text-red-300`}>
            {error}
          </div>
        )}

        {/* Create new post */}
        <NewPostForm onCreated={loadData} />

        {/* Static articles */}
        <StaticArticlesSection hiddenSlugs={hiddenSlugs} onToggle={loadData} />

        {/* DB Posts table */}
        <Card variant="glass">
          <CardHeader
            title="Blog Posts"
            subtitle={`Total: ${posts.length} • Published: ${publishedCount} • Drafts: ${draftCount}`}
          />
          <CardBody>
            {loading ? (
              <div className={`${typography.body} ${textColors.secondary}`}>Loading blog posts…</div>
            ) : posts.length === 0 ? (
              <div className={`${typography.body} ${textColors.secondary}`}>No blog posts yet. Create one above.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className={`w-full text-left ${typography.bodySmall}`}>
                  <thead>
                    <tr className={`border-b ${borders.glass}`}>
                      <th className={`py-2 pr-4 ${typography.label} ${textColors.secondary}`}>Title</th>
                      <th className={`py-2 pr-4 ${typography.label} ${textColors.secondary}`}>Author</th>
                      <th className={`py-2 pr-4 ${typography.label} ${textColors.secondary}`}>Status</th>
                      <th className={`py-2 pr-4 ${typography.label} ${textColors.secondary}`}>Created</th>
                      <th className={`py-2 pr-4 ${typography.label} ${textColors.secondary}`}>Preview</th>
                      <th className={`py-2 ${typography.label} ${textColors.secondary}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((post) => (
                      <EditPostRow key={post.id} post={post} onSaved={loadData} onDeleted={loadData} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default BlogAdminPage;
